<template>
    <div :class="[`block-office-panels`]" data-aos="custom-fade-in">
        <div v-if="json" :id="swiperContainerId" class="swiper-container">
            <div class="swiper-wrapper">
                <div class="swiper-slide"
                     v-for="(item, index) in json"
                     :key="`op-${index}-${Math.random()}`"
                >
                    <div class="main-anchor nolink">
                        <div class="flx">
                            <div class="flx-l">
                                <div class="bgimg-outer">
                                    <div :class="['bgimg']" :style="item.officeImage?.[0].bgStyle"></div>

                                    <div class="swiper-nav-elements swiper-nav-elements-mobile">
                                        <div class="swiper-pagination"></div>
                                        <div class="swiper-buttons">
                                            <div class="swiper-button swiper-button-prev"></div>
                                            <div class="swiper-button swiper-button-next"></div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                            <div class="flx-r">
                                <div class="items">
                                    <div class="items2">
                                        <h2 ref="titles" v-html="item.title"></h2>
                                        <div class="caption" ref="captions" v-html="item.caption" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="swiper-nav-elements swiper-nav-elements-desktop">
                <div class="swiper-pagination"></div>
                <div class="swiper-buttons">
                    <div class="swiper-button swiper-button-prev"></div>
                    <div class="swiper-button swiper-button-next"></div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import gsap from '@/plugins/gsap';
import SplitText from '@/plugins/gsap/SplitText';
import Swiper, { Navigation, Pagination, Keyboard, EffectFade } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import gh from '@/helpers/gh';
Swiper.use([Navigation, Pagination, Keyboard, EffectFade]);
gsap.registerPlugin(SplitText);

export default {
    name: "FeaturedSlides",
    data() {
        return {
            gh: gh,
            document: document,
            slider: null,
            swiperContainerId: null,
            swiperContainerSel: null,
            timeoutId: null,
            isDragging: false,
            didOnce: false,
        };
    },
    props: {
        json: {
            type: Object,
            required: true,
            default: null,
        },
    },
    mounted() {
        this.swiperContainerId = 'opfs-' + Date.now();
        this.swiperContainerSel = '#' + this.swiperContainerId;
        window.addEventListener('resize', this.normalizeCaptionsSize);
        window.addEventListener('scroll', this.onScroll);
        this.$nextTick(() => {
            this.init();
            this.normalizeCaptionsSize();
        });
    },
    beforeUnmount() {
        clearTimeout(this.timeoutId);
        window.removeEventListener('resize', this.normalizeCaptionsSize);
        window.removeEventListener('scroll', this.onScroll);
        if (this.slider) {
            this.slider.destroy();
        }
    },
    methods: {
        init() {
            this.slider = new Swiper(this.swiperContainerSel, {
                init: false,
                effect: 'fade',
                fadeEffect: {
                    crossFade: true
                },
                speed: 750,
                grabCursor: false,
                loop: (this.json.length > 1),
                allowSlidePrev: (this.json.length > 1),
                allowSlideNext: (this.json.length > 1),
                simulateTouch: (this.json.length > 1),
                slidesPerView: 1,
                preventClicks: false,
                preventClicksPropagation: false,
                touchStartPreventDefault: true,
                allowTouchMove: true,
                navigation: {
                    nextEl: this.swiperContainerSel + ' .swiper-button-next',
                    prevEl: this.swiperContainerSel + ' .swiper-button-prev'
                },
                pagination: {
                    el: this.swiperContainerSel + ' .swiper-pagination',
                    type: 'bullets',
                    clickable: true,
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
            });

            this.slider.on('slideChangeTransitionStart', this.onSlideChange);
            this.slider.on('afterInit', this.afterInit);
            setTimeout(() => this.slider.init(), 100);
        },
        normalizeCaptionsSize() {
            this.$nextTick(() => {
                let captions = this.$refs.captions;
                if (!captions || captions.length === 0) return;
                let maxHeight = 0;
                captions.forEach(caption => {
                    caption.style.height = 'auto';
                    maxHeight = Math.max(maxHeight, caption.offsetHeight);
                });
                captions.forEach(caption => caption.style.height = maxHeight + 'px');
            });
        },
        afterInit() {
            this.slider.slides.forEach(slide => {
                let title = slide.querySelector("h2");
                let caption = slide.querySelector(".caption");
                let bgImage = slide.querySelector(".bgimg");

                if (bgImage) {
                    bgImage.style.filter = "grayscale(100%)";
                }
                if (title) {
                    title.style.opacity = 0;
                }
                if (caption) {
                    caption.style.opacity = 0;
                }
            });
        },
        onSlideChange() {
            if (!this.didOnce) return;
            this.$nextTick(() => {
                let activeSlide = this.slider.slides[this.slider.activeIndex];
                if (activeSlide) {
                    this.animateSlide(activeSlide);
                }
            });
        },
        animateSlide(slide) {
            let title = slide.querySelector("h2");
            let caption = slide.querySelector(".caption");
            let bgImage = slide.querySelector(".bgimg");

            if (bgImage) {
                gsap.fromTo(bgImage, {filter: "grayscale(100%)"}, {
                    filter: "grayscale(0%)",
                    duration: 0.75,
                    ease: "power2.out",
                    delay: 0.2,
                });
            }
            if (title) {
                let splitTitle = new SplitText(title, {type: "words"});
                gsap.set(title, {opacity: 1});
                gsap.fromTo(splitTitle.words, {opacity: 0, y: 15}, {
                    opacity: 1,
                    y: 0,
                    duration: 0.75,
                    delay: 0.3,
                    ease: "power2.out",
                    stagger: 0.1
                });
            }
            if (caption) {
                gsap.fromTo(caption, {opacity: 0, y: 15}, {
                    opacity: 1,
                    y:0,
                    duration: 0.75,
                    delay: 0.5,
                    ease: "power2.out"
                });
            }
        },
        onScroll() {
            if (this.didOnce) return;
            let panel = document.querySelector(this.swiperContainerSel);
            if (!panel) return;
            let rect = panel.getBoundingClientRect();
            let viewportHeight = window.innerHeight;
            if (rect.top < viewportHeight * 0.5 && rect.bottom > 0) {
                this.didOnce = true;
                this.animateSlide(this.slider.slides[this.slider.activeIndex]);
                //window.removeEventListener('scroll', this.onScroll);
            }
        }
    },
};
</script>
